// Typed wrappers around `/api/staff/statistics/*`. Mirrors the pattern in
// shared/src/api/exhibits.ts + zones.ts. Stats endpoints are all
// authenticated staff-only GETs; the CSV export route returns a Blob and
// has its own helper.
//
// PR 12 ST 8 — consumers wire in at ST 9 (staff/src/features/stats/).

import { getApiClient } from "./client";
import type { components } from "./types";

export type StatsSummary = components["schemas"]["StatsSummary"];
export type HourlyVisitorCount = components["schemas"]["HourlyVisitorCount"];
export type ItemViewCount = components["schemas"]["ItemViewCount"];
export type DailyVisitorCount = components["schemas"]["DailyVisitorCount"];

// /staff/statistics/visitors returns an array of free-form rows; the
// service anonymises visitor_id but doesn't publish a fixed schema yet.
export type VisitorStatsRow = Record<string, unknown>;

export interface HourlyParams {
  /** YYYY-MM-DD */
  date: string;
  /** Browser timezone offset in minutes, e.g. -480 for UTC+8. */
  tz_offset?: number;
}

export interface RangeParams {
  /** YYYY-MM-DD */
  start_date: string;
  /** YYYY-MM-DD */
  end_date: string;
}

export interface ItemsParams extends RangeParams {
  item_type?: string | null;
  zone_id?: string | null;
  exhibition_id?: string | null;
}

export interface ExportParams extends RangeParams {
  export_type?: string;
}

export async function getStatsSummary(): Promise<StatsSummary> {
  const { data } = await getApiClient().get<StatsSummary>(
    "/staff/statistics/summary",
    {
      // Cache-buster preserved from legacy — the staff dashboard refresh
      // button relies on this to bypass any intermediate caches.
      params: { _t: Date.now() },
    },
  );
  return data;
}

export async function getHourlyStats(
  params: HourlyParams,
): Promise<HourlyVisitorCount[]> {
  const { data } = await getApiClient().get<HourlyVisitorCount[]>(
    "/staff/statistics/hourly",
    { params: { ...params, _t: Date.now() } },
  );
  return data;
}

export async function getItemStats(
  params: ItemsParams,
): Promise<ItemViewCount[]> {
  const { data } = await getApiClient().get<ItemViewCount[]>(
    "/staff/statistics/items",
    { params: { ...params, _t: Date.now() } },
  );
  return data;
}

export async function getDailyCounts(
  params: RangeParams,
): Promise<DailyVisitorCount[]> {
  const { data } = await getApiClient().get<DailyVisitorCount[]>(
    "/staff/statistics/daily-counts",
    { params },
  );
  return data;
}

export async function getVisitorStats(
  params: RangeParams,
): Promise<VisitorStatsRow[]> {
  const { data } = await getApiClient().get<VisitorStatsRow[]>(
    "/staff/statistics/visitors",
    { params },
  );
  return data;
}

// CSV export — returns a Blob; downstream caller wires the <a download>
// dance. Not a React-Query target (no cached state to manage).
export async function exportStatsCsv(params: ExportParams): Promise<Blob> {
  const { data } = await getApiClient().get<Blob>("/staff/statistics/export", {
    params,
    responseType: "blob",
  });
  return data;
}
